import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import HelpModal from '../../../components/HelpModal/HelpModal'
import projectExpenseService from '../../../services/projectExpenseService'
import projectService from '../../../services/projectService'
import employeeMasterService from '../../../services/employeeMasterService'
import expenseCategoryMasterService from '../../../services/expenseCategoryMasterService'
import paymentMethodMasterService from '../../../services/paymentMethodMasterService'
import taskService from '../../../services/taskService'
import autoIncrementService from '../../../services/autoIncrementService'
import { 
  DollarSign,
  Calendar,
  User,
  FileText,
  Receipt,
  RotateCcw,
  AlertCircle,
  HelpCircle
} from 'lucide-react'

const ProjectExpenseForm = ({ mode = 'create', expenseId = null, initialData = null, breadcrumbItems: customBreadcrumbItems = null }) => {
  const navigate = useNavigate()
  const [showHelp, setShowHelp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [expenseDataLoading, setExpenseDataLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('basic')
  const [masterDataLoaded, setMasterDataLoaded] = useState(false)
  const [isLoadingExpenseNumber, setIsLoadingExpenseNumber] = useState(false)
  const hasLoadedExpenseDataRef = useRef(false)
  const isEditMode = mode === 'edit'
  const isViewMode = mode === 'view'
  const isReadOnly = isViewMode

  const [masterData, setMasterData] = useState({
    projects: [],
    employees: [],
    expenseCategories: [],
    paymentMethods: [],
    tasks: []
  })

  const [formData, setFormData] = useState({
    expenseNumber: '',
    project: '',
    task: '',
    employee: '',
    expenseCategory: '',
    expenseDate: new Date().toISOString().split('T')[0],
    amount: 0,
    currency: 'INR',
    description: '',
    receiptNumber: '',
    receiptDate: '',
    hasReceipt: false,
    isBillable: true,
    billingAmount: 0,
    billingCurrency: 'INR',
    isTaxable: false,
    taxRate: 0,
    taxAmount: 0,
    totalAmount: 0,
    paymentMethod: '',
    paymentDate: '',
    status: 'Draft',
    location: '',
    vendorName: '',
    notes: ''
  })

  const [errors, setErrors] = useState({})

  // Load master data on component mount
  useEffect(() => {
    if (!masterDataLoaded && !loading) {
      loadMasterData()
    }
  }, [masterDataLoaded, loading])

  // Fetch next expense number (only for new expenses)
  useEffect(() => {
    if (!isEditMode && !isViewMode && !isLoadingExpenseNumber) {
      fetchNextExpenseNumber()
    }
  }, [isEditMode, isViewMode])

  // Load expense data for edit/view mode - wait for master data to be loaded first
  useEffect(() => {
    if ((isEditMode || isViewMode) && expenseId && masterDataLoaded && !hasLoadedExpenseDataRef.current) {
      if (initialData) {
        populateFormData(initialData)
        hasLoadedExpenseDataRef.current = true
      } else if (!expenseDataLoading) {
        loadExpenseData()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenseId, isEditMode, isViewMode, masterDataLoaded])

  const loadExpenseData = async () => {
    if (hasLoadedExpenseDataRef.current) {
      return // Already loaded, prevent duplicate calls
    }
    
    try {
      setExpenseDataLoading(true)
      hasLoadedExpenseDataRef.current = true
      const response = await projectExpenseService.getProjectExpenseById(expenseId)
      
      if (response.success && response.data) {
        populateFormData(response.data)
      } else {
        toast.error(response.message || 'Failed to load expense')
        navigate('/projects/expenses/list')
      }
    } catch (error) {
      console.error('Error loading expense:', error)
      toast.error('Failed to load expense')
      navigate('/projects/expenses/list')
    } finally {
      setExpenseDataLoading(false)
    }
  }

  const populateFormData = (data) => {
    // Extract IDs from populated objects
    let expenseCategoryValue = ''
    if (data.expenseCategory) {
      if (typeof data.expenseCategory === 'object' && data.expenseCategory !== null) {
        expenseCategoryValue = String(data.expenseCategory._id || data.expenseCategory.id || '')
      } else {
        expenseCategoryValue = String(data.expenseCategory)
      }
    }

    let paymentMethodValue = ''
    if (data.paymentMethod) {
      if (typeof data.paymentMethod === 'object' && data.paymentMethod !== null) {
        paymentMethodValue = String(data.paymentMethod._id || data.paymentMethod.id || '')
      } else {
        paymentMethodValue = String(data.paymentMethod)
      }
    }

    // Extract task ID
    let taskValue = ''
    if (data.task) {
      if (typeof data.task === 'object' && data.task !== null) {
        taskValue = String(data.task._id || data.task.id || '')
      } else {
        taskValue = String(data.task)
      }
    }

    // Extract project ID
    let projectValue = ''
    if (data.project) {
      if (typeof data.project === 'object' && data.project !== null) {
        projectValue = String(data.project._id || data.project.id || '')
      } else {
        projectValue = String(data.project)
      }
    }

    setFormData({
      expenseNumber: data.expenseNumber || '',
      project: projectValue,
      task: taskValue,
      employee: data.employee?._id || data.employee || '',
      expenseCategory: expenseCategoryValue,
      expenseDate: data.expenseDate ? new Date(data.expenseDate).toISOString().split('T')[0] : '',
      amount: data.amount || 0,
      currency: data.currency || 'INR',
      description: data.description || '',
      receiptNumber: data.receiptNumber || '',
      receiptDate: data.receiptDate ? new Date(data.receiptDate).toISOString().split('T')[0] : '',
      hasReceipt: data.hasReceipt || false,
      isBillable: data.isBillable !== undefined ? data.isBillable : true,
      billingAmount: data.billingAmount || 0,
      billingCurrency: data.billingCurrency || 'INR',
      isTaxable: data.isTaxable || false,
      taxRate: data.taxRate || 0,
      taxAmount: data.taxAmount || 0,
      totalAmount: data.totalAmount || 0,
      paymentMethod: paymentMethodValue,
      paymentDate: data.paymentDate ? new Date(data.paymentDate).toISOString().split('T')[0] : '',
      status: data.status || 'Draft',
      location: data.location || '',
      vendorName: data.vendorName || '',
      notes: data.notes || ''
    })
    setErrors({})
  }

  const fetchNextExpenseNumber = async () => {
    try {
      setIsLoadingExpenseNumber(true)
      const response = await autoIncrementService.getCurrentValue('PROJECT-EXPENSE')
      
      if (response.success && response.data) {
        const sequence = response.data
        if (sequence.formattedValue) {
          setFormData(prev => ({
            ...prev,
            expenseNumber: sequence.formattedValue
          }))
        } else {
          setFormData(prev => ({
            ...prev,
            expenseNumber: 'EXP-000001'
          }))
        }
      } else {
        setFormData(prev => ({
          ...prev,
          expenseNumber: 'EXP-000001'
        }))
      }
    } catch (error) {
      console.error('Error fetching next expense number:', error)
      setFormData(prev => ({
        ...prev,
        expenseNumber: 'EXP-000001'
      }))
    } finally {
      setIsLoadingExpenseNumber(false)
    }
  }

  const loadMasterData = async () => {
    try {
      setLoading(true)
      const [
        projectsRes,
        employeesRes,
        categoriesRes,
        paymentMethodsRes,
        tasksRes
      ] = await Promise.allSettled([
        projectService.getAllProjects({ limit: 1000, status: 'Active' }),
        employeeMasterService.getAllEmployees({ limit: 1000, status: 'Active' }),
        expenseCategoryMasterService.getActiveCategories(),
        paymentMethodMasterService.getActivePaymentMethods(),
        taskService.getAllTasks({ limit: 1000 })
      ])

      const getData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            return res.data
          }
          if (Array.isArray(res)) {
            return res
          }
          if (res.projects) {
            return res.projects
          }
          if (res.tasks) {
            return res.tasks
          }
        }
        return []
      }

      const getEmployeesData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            return res.data
          }
        }
        return []
      }

      const getCategoriesData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            if (Array.isArray(res.data)) {
              return res.data
            }
            return res.data
          }
          if (Array.isArray(res)) {
            return res
          }
        }
        return []
      }

      const getPaymentMethodsData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            if (Array.isArray(res.data)) {
              return res.data
            }
            return res.data
          }
          if (Array.isArray(res)) {
            return res
          }
        }
        return []
      }

      setMasterData({
        projects: getData(projectsRes),
        employees: getEmployeesData(employeesRes),
        expenseCategories: getCategoriesData(categoriesRes),
        paymentMethods: getPaymentMethodsData(paymentMethodsRes),
        tasks: getData(tasksRes)
      })

      const failedRequests = [
        { name: 'Projects', result: projectsRes },
        { name: 'Employees', result: employeesRes },
        { name: 'Expense Categories', result: categoriesRes },
        { name: 'Payment Methods', result: paymentMethodsRes },
        { name: 'Tasks', result: tasksRes }
      ].filter(item => item.result.status === 'rejected')

      if (failedRequests.length > 0) {
        console.warn('Some master data failed to load:', failedRequests.map(r => r.name))
        // Check if errors were already handled by interceptor
        const hasNetworkError = failedRequests.some(r => {
          const error = r.result.reason
          return error && (error._errorHandled || error.request)
        })
        if (!hasNetworkError) {
          if (failedRequests.length === 4) {
            toast.error('Unable to connect to backend server. Please ensure the server is running.')
          } else {
            toast.warning(`Failed to load some data: ${failedRequests.map(r => r.name).join(', ')}`)
          }
        }
      }
      
      setMasterDataLoaded(true)
    } catch (error) {
      console.error('Error loading master data:', error)
      if (!error._errorHandled && !error.request) {
        toast.error('Failed to load master data. Please check your connection.')
      }
      setMasterDataLoaded(true)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      }

      // Auto-calculate tax and total when amount or tax rate changes
      if (field === 'amount' || field === 'taxRate') {
        const amount = field === 'amount' ? parseFloat(value) || 0 : prev.amount
        const taxRate = field === 'taxRate' ? parseFloat(value) || 0 : prev.taxRate
        const taxAmount = (amount * taxRate) / 100
        const totalAmount = amount + taxAmount

        updated.taxAmount = taxAmount
        updated.totalAmount = totalAmount
        if (prev.isBillable) {
          updated.billingAmount = totalAmount
        }
      }

      // Auto-set billing amount if billable
      if (field === 'isBillable' && value) {
        updated.billingAmount = updated.totalAmount || updated.amount
      }

      return updated
    })
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.project) {
      newErrors.project = 'Project is required'
    }
    if (!formData.employee) {
      newErrors.employee = 'Employee is required'
    }
    if (!formData.expenseCategory) {
      newErrors.expenseCategory = 'Expense category is required'
    }
    if (!formData.expenseDate) {
      newErrors.expenseDate = 'Expense date is required'
    }
    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'Amount must be greater than 0'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error('Please fix the errors in the form')
      return
    }

    try {
      setLoading(true)

      let response
      if (isEditMode) {
        // Remove expenseNumber when updating - backend will not allow it to be changed
        const { expenseNumber, ...updateData } = formData
        response = await projectExpenseService.updateProjectExpense(expenseId, updateData)
      } else {
        // Remove expenseNumber when creating - backend will auto-generate it via pre-save hook
        // The preview number is just for display, not for saving
        const { expenseNumber, ...createData } = formData
        response = await projectExpenseService.createProjectExpense(createData)
      }

      if (response.success) {
        toast.success(`Project expense ${isEditMode ? 'updated' : 'created'} successfully`)
        navigate('/projects/expenses/list')
      } else {
        toast.error(response.message || `Failed to ${isEditMode ? 'update' : 'create'} expense`)
      }
    } catch (error) {
      console.error('Error saving expense:', error)
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} expense`)
    } finally {
      setLoading(false)
    }
  }

  const handleTestData = () => {
    const today = new Date()
    const expenseDate = new Date(today)
    expenseDate.setDate(today.getDate() - 1)

    setFormData(prev => ({
      ...prev, // Preserve existing expenseNumber
      project: masterData.projects.length > 0 ? (masterData.projects[0]._id || masterData.projects[0].id) : '',
      task: masterData.tasks.length > 0 ? (masterData.tasks[0]._id || masterData.tasks[0].id) : '',
      employee: masterData.employees.length > 0 ? (masterData.employees[0]._id || masterData.employees[0].id) : '',
      expenseCategory: masterData.expenseCategories.length > 0 ? (masterData.expenseCategories[0]._id || masterData.expenseCategories[0].id) : '',
      expenseDate: expenseDate.toISOString().split('T')[0],
      amount: 5000,
      currency: 'INR',
      description: 'Travel expense for client meeting',
      receiptNumber: 'RCP-001',
      receiptDate: expenseDate.toISOString().split('T')[0],
      hasReceipt: true,
      isBillable: true,
      billingAmount: 5000,
      isTaxable: true,
      taxRate: 18,
      taxAmount: 900,
      totalAmount: 5900,
      paymentMethod: masterData.paymentMethods.length > 0 ? String(masterData.paymentMethods[0]._id || masterData.paymentMethods[0].id) : '',
      location: 'Mumbai',
      vendorName: 'Travel Agency',
      notes: 'Client meeting expense'
    }))
    setErrors({})
    toast.success('Test data loaded successfully!')
  }

  // Listen for F1 key to populate test data (only in create mode)
  useEffect(() => {
    if (isEditMode || isViewMode) return // Don't add listener in edit/view mode

    const handleKeyPress = (event) => {
      // Check if F1 key is pressed (keyCode 112 or key === 'F1')
      if (event.key === 'F1' || event.keyCode === 112) {
        event.preventDefault() // Prevent browser help menu
        handleTestData()
      }
    }

    // Add event listener
    window.addEventListener('keydown', handleKeyPress)

    // Cleanup on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [isEditMode, isViewMode, masterData]) // Include dependencies used by handleTestData

  const breadcrumbItems = customBreadcrumbItems || [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Transactions', path: '/projects?tab=transactions', onClick: () => navigate('/projects?tab=transactions') },
    { label: 'Project Expenses', path: '/projects/expenses/list', onClick: () => navigate('/projects/expenses/list') },
    { label: isEditMode ? 'Edit Expense' : isViewMode ? 'View Expense' : 'Create New', path: isEditMode ? `/projects/expenses/edit/${expenseId}` : isViewMode ? `/projects/expenses/view/${expenseId}` : '/projects/expenses/new', isCurrent: true }
  ]

  const tabs = [
    { id: 'basic', label: 'Basic Information', icon: DollarSign },
    { id: 'details', label: 'Details', icon: FileText },
    { id: 'billing', label: 'Billing & Tax', icon: Receipt },
    { id: 'additional', label: 'Additional Info', icon: Calendar }
  ]

  const helpContent = (
    <>
      <h3>What is Project Expense Entry?</h3>
      <p>Project Expense Entry lets you <strong>create and manage project-related expenses</strong> for tracking and reimbursement. You link an expense to a project and optionally a task, with category, amount, date, receipt, and billing/tax details.</p>
      <h3>Tabs</h3>
      <p><strong>Basic Information</strong> – Expense number, project, task, category, amount, currency, expense date, payment method, vendor, description.</p>
      <p><strong>Details</strong> – Additional expense details and attachments.</p>
      <p><strong>Billing &amp; Tax</strong> – Billable flag, tax, and billing-related fields.</p>
      <p><strong>Additional Info</strong> – Notes and other optional fields.</p>
      <h3>How to use</h3>
      <p>Select a <strong>project</strong> (and optionally task), choose category, enter amount and date. Use <strong>Create Expense</strong> to save. Submit for approval from the list when required.</p>
    </>
  )

  // Filter tasks by selected project
  // Include the current task even if it doesn't match the project filter (for edit mode)
  const filteredTasks = formData.project
    ? masterData.tasks.filter(task => {
        const taskProjectId = task.project?._id || task.project
        const taskId = task._id || task.id
        // Include task if it matches the project OR if it's the currently selected task (for edit mode)
        return String(taskProjectId) === String(formData.project) || 
               (formData.task && String(taskId) === String(formData.task))
      })
    : formData.task
      ? masterData.tasks.filter(task => {
          const taskId = task._id || task.id
          return String(taskId) === String(formData.task)
        })
      : []

  if (expenseDataLoading) {
    return (
      <div className="sales-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading expense data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Project Expense Entry</h1>
          <p>Create and manage project expense information</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Project Expense Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          {tabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                type="button"
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            )
          })}
        </div>

        <form className="form-content" onSubmit={handleSubmit}>
          {/* Validation Error Summary */}
          {Object.keys(errors).length > 0 && (
            <div style={{
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #dc2626',
              borderRadius: '4px',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <AlertCircle size={16} style={{ color: '#DC2626' }} />
                <strong style={{ color: '#DC2626' }}>Please fix the following errors:</strong>
              </div>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#DC2626' }}>
                {Object.entries(errors).map(([field, message]) => {
                  const fieldNames = {
                    project: 'Project',
                    employee: 'Employee',
                    expenseCategory: 'Expense Category',
                    expenseDate: 'Expense Date',
                    amount: 'Amount'
                  }
                  return (
                    <li key={field} style={{ marginBottom: '4px', cursor: 'pointer' }}
                        onClick={() => {
                          const element = document.getElementById(field) || document.querySelector(`[name="${field}"]`)
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                            element.focus()
                          }
                        }}>
                      <strong>{fieldNames[field] || field}:</strong> {message}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'basic' && (
              <div>
                <div className="section-header">
                  <h3>Basic Information</h3>
                </div>
                
                {/* Row 1: Expense Number, Project, Task */}
                <div className="grid-row-3">
                  {/* Expense Number */}
                  <div className="form-group">
                    <label htmlFor="expenseNumber" className="form-label">
                      Expense Number
                    </label>
                    <input
                      id="expenseNumber"
                      type="text"
                      value={isLoadingExpenseNumber ? 'Loading...' : formData.expenseNumber}
                      className="form-input"
                      placeholder="Auto-generated"
                      readOnly
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                    />
                  </div>

                  {/* Project */}
                  <div className="form-group">
                    <label htmlFor="project" className="form-label required">
                      Project
                    </label>
                    <select
                      id="project"
                      value={formData.project}
                      onChange={(e) => handleInputChange('project', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Project</option>
                      {masterData.projects.map((project) => {
                        const projectId = project._id || project.id
                        return (
                          <option key={projectId} value={String(projectId)}>
                            {project.projectNumber} - {project.projectName}
                          </option>
                        )
                      })}
                    </select>
                    {errors.project && (
                      <p className="form-error">{errors.project}</p>
                    )}
                  </div>

                  {/* Task */}
                  <div className="form-group">
                    <label htmlFor="task" className="form-label">
                      Task
                    </label>
                    <select
                      id="task"
                      value={formData.task}
                      onChange={(e) => handleInputChange('task', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly || !formData.project}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Task</option>
                      {filteredTasks.map((task) => {
                        const taskId = task._id || task.id
                        return (
                          <option key={taskId} value={String(taskId)}>
                            {task.taskNumber} - {task.taskName}
                          </option>
                        )
                      })}
                    </select>
                  </div>
                </div>

                {/* Row 2: Employee, Expense Category, Expense Date */}
                <div className="grid-row-3">
                  {/* Employee */}
                  <div className="form-group">
                    <label htmlFor="employee" className="form-label required">
                      Employee
                    </label>
                    <select
                      id="employee"
                      value={formData.employee}
                      onChange={(e) => handleInputChange('employee', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Employee</option>
                      {masterData.employees.map((employee) => {
                        const employeeId = employee._id || employee.id
                        return (
                          <option key={employeeId} value={String(employeeId)}>
                            {employee.employeeName || employee.name}
                          </option>
                        )
                      })}
                    </select>
                    {errors.employee && (
                      <p className="form-error">{errors.employee}</p>
                    )}
                  </div>

                  {/* Expense Category */}
                  <div className="form-group">
                    <label htmlFor="expenseCategory" className="form-label required">
                      Expense Category
                    </label>
                    <select
                      id="expenseCategory"
                      value={formData.expenseCategory}
                      onChange={(e) => handleInputChange('expenseCategory', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Category</option>
                      {masterData.expenseCategories.map((category) => {
                        const categoryId = category._id || category.id
                        return (
                          <option key={categoryId} value={String(categoryId)}>
                            {category.categoryName || category.name}
                          </option>
                        )
                      })}
                    </select>
                    {errors.expenseCategory && (
                      <p className="form-error">{errors.expenseCategory}</p>
                    )}
                  </div>

                  {/* Expense Date */}
                  <div className="form-group">
                    <label htmlFor="expenseDate" className="form-label required">
                      Expense Date
                    </label>
                    <input
                      id="expenseDate"
                      type="date"
                      value={formData.expenseDate}
                      onChange={(e) => handleInputChange('expenseDate', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.expenseDate && (
                      <p className="form-error">{errors.expenseDate}</p>
                    )}
                  </div>
                </div>

                {/* Row 3: Amount, Currency, Status */}
                <div className="grid-row-3">
                  {/* Amount */}
                  <div className="form-group">
                    <label htmlFor="amount" className="form-label required">
                      Amount
                    </label>
                    <input
                      id="amount"
                      type="number"
                      value={formData.amount}
                      onChange={(e) => handleInputChange('amount', e.target.value)}
                      className="form-input"
                      min="0"
                      step="0.01"
                      placeholder="Enter amount"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.amount && (
                      <p className="form-error">{errors.amount}</p>
                    )}
                  </div>

                  {/* Currency */}
                  <div className="form-group">
                    <label htmlFor="currency" className="form-label">
                      Currency
                    </label>
                    <select
                      id="currency"
                      value={formData.currency}
                      onChange={(e) => handleInputChange('currency', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="INR">INR</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="CAD">CAD</option>
                      <option value="AUD">AUD</option>
                    </select>
                  </div>

                  {/* Status */}
                  <div className="form-group">
                    <label htmlFor="status" className="form-label">
                      Status
                    </label>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="Draft">Draft</option>
                      <option value="Submitted">Submitted</option>
                      <option value="Approved">Approved</option>
                      <option value="Rejected">Rejected</option>
                      <option value="Paid">Paid</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'details' && (
              <div>
                <div className="section-header">
                  <h3>Expense Details</h3>
                </div>

              <div className="grid-row-3">
                {/* Description */}
                <div className="form-group">
                  <label htmlFor="description" className="form-label">
                    Description
                  </label>
                  <input
                    id="description"
                    type="text"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="form-input"
                    placeholder="Enter expense description"
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                    style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                  />
                </div>

                {/* Receipt Number */}
                <div className="form-group">
                  <label htmlFor="receiptNumber" className="form-label">
                    Receipt Number
                  </label>
                  <input
                    id="receiptNumber"
                    type="text"
                    value={formData.receiptNumber}
                    onChange={(e) => handleInputChange('receiptNumber', e.target.value)}
                    className="form-input"
                    placeholder="Enter receipt number"
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                    style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                  />
                </div>

                {/* Receipt Date */}
                <div className="form-group">
                  <label htmlFor="receiptDate" className="form-label">
                    Receipt Date
                  </label>
                  <input
                    id="receiptDate"
                    type="date"
                    value={formData.receiptDate}
                    onChange={(e) => handleInputChange('receiptDate', e.target.value)}
                    className="form-input"
                    disabled={isReadOnly}
                    style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                  />
                </div>

                {/* Has Receipt */}
                <div className="form-group">
                  <label htmlFor="hasReceipt" className="form-label">
                    <input
                      type="checkbox"
                      id="hasReceipt"
                      checked={formData.hasReceipt}
                      onChange={(e) => handleInputChange('hasReceipt', e.target.checked)}
                      disabled={isReadOnly}
                      style={isReadOnly ? { cursor: 'not-allowed' } : {}}
                    />
                    Has Receipt
                  </label>
                </div>

                {/* Location */}
                <div className="form-group">
                  <label htmlFor="location" className="form-label">
                    Location
                  </label>
                  <input
                    id="location"
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    className="form-input"
                    placeholder="Enter location"
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                    style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                  />
                </div>

                {/* Vendor Name */}
                <div className="form-group">
                  <label htmlFor="vendorName" className="form-label">
                    Vendor Name
                  </label>
                  <input
                    id="vendorName"
                    type="text"
                    value={formData.vendorName}
                    onChange={(e) => handleInputChange('vendorName', e.target.value)}
                    className="form-input"
                    placeholder="Enter vendor name"
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                    style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                  />
                </div>
              </div>
              </div>
            )}

            {activeTab === 'billing' && (
              <div>
                <div className="section-header">
                  <h3>Billing & Tax Information</h3>
                </div>

                {/* Is Billable Row */}
                <div className="grid-row-3">
                  {/* Is Billable */}
                  <div className="form-group">
                    <label htmlFor="isBillable" className="form-label">
                      <input
                        type="checkbox"
                        id="isBillable"
                        checked={formData.isBillable}
                        onChange={(e) => handleInputChange('isBillable', e.target.checked)}
                        disabled={isReadOnly}
                        style={isReadOnly ? { cursor: 'not-allowed' } : {}}
                      />
                      Is Billable
                    </label>
                  </div>

                  {/* Billing Amount */}
                  {formData.isBillable && (
                    <>
                      <div className="form-group">
                        <label htmlFor="billingAmount" className="form-label">
                          Billing Amount
                        </label>
                        <input
                          id="billingAmount"
                          type="number"
                          value={formData.billingAmount}
                          onChange={(e) => handleInputChange('billingAmount', e.target.value)}
                          className="form-input"
                          min="0"
                          step="0.01"
                          disabled={isReadOnly}
                          style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="billingCurrency" className="form-label">
                          Billing Currency
                        </label>
                        <select
                          id="billingCurrency"
                          value={formData.billingCurrency}
                          onChange={(e) => handleInputChange('billingCurrency', e.target.value)}
                          className="form-input"
                          disabled={isReadOnly}
                          style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                        >
                          <option value="INR">INR</option>
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                          <option value="CAD">CAD</option>
                          <option value="AUD">AUD</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>

                {/* Is Taxable Row */}
                <div className="grid-row-3">
                  <div className="form-group">
                    <label htmlFor="isTaxable" className="form-label">
                      <input
                        type="checkbox"
                        id="isTaxable"
                        checked={formData.isTaxable}
                        onChange={(e) => handleInputChange('isTaxable', e.target.checked)}
                        disabled={isReadOnly}
                        style={isReadOnly ? { cursor: 'not-allowed' } : {}}
                      />
                      Is Taxable
                    </label>
                  </div>

                  {/* Tax Rate */}
                  {formData.isTaxable && (
                    <>
                      <div className="form-group">
                        <label htmlFor="taxRate" className="form-label">
                          Tax Rate (%)
                        </label>
                        <input
                          id="taxRate"
                          type="number"
                          value={formData.taxRate}
                          onChange={(e) => handleInputChange('taxRate', e.target.value)}
                          className="form-input"
                          min="0"
                          max="100"
                          step="0.01"
                          disabled={isReadOnly}
                          style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="taxAmount" className="form-label">
                          Tax Amount
                        </label>
                        <input
                          id="taxAmount"
                          type="number"
                          value={formData.taxAmount}
                          readOnly
                          className="form-input"
                          style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="totalAmount" className="form-label">
                          Total Amount
                        </label>
                        <input
                          id="totalAmount"
                          type="number"
                          value={formData.totalAmount}
                          readOnly
                          className="form-input"
                          style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* Payment Method and Date */}
                <div className="grid-row-3">
                  <div className="form-group">
                    <label htmlFor="paymentMethod" className="form-label">
                      Payment Method
                    </label>
                    <select
                      id="paymentMethod"
                      value={formData.paymentMethod}
                      onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Payment Method</option>
                      {masterData.paymentMethods.map((method) => {
                        const methodId = method._id || method.id
                        return (
                          <option key={methodId} value={String(methodId)}>
                            {method.paymentMethodName}
                          </option>
                        )
                      })}
                    </select>
                    {errors.paymentMethod && (
                      <p className="form-error">{errors.paymentMethod}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="paymentDate" className="form-label">
                      Payment Date
                    </label>
                    <input
                      id="paymentDate"
                      type="date"
                      value={formData.paymentDate}
                      onChange={(e) => handleInputChange('paymentDate', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'additional' && (
              <div>
                <div className="section-header">
                  <h3>Additional Information</h3>
                </div>
                
                <div className="form-group">
                  <label htmlFor="notes" className="form-label">
                    Notes
                  </label>
                  <input
                    id="notes"
                    type="text"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    className="form-input"
                    placeholder="Enter additional notes"
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                    style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          {!isViewMode && (
            <div className="form-actions" style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => navigate('/projects/expenses/list')}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : isEditMode ? 'Update Expense' : 'Create Expense'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Project Expense Entry – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default ProjectExpenseForm

